import React, { useRef, useState, useEffect, useMemo } from 'react';
import { ResourceManager } from './components/ResourceManager';
import { formatUGX } from '@/utils/currency';
import { useSupabaseResource } from '@/hooks/useSupabaseResource';
import { supabase } from '@/lib/supabaseClient';
import { Download, Upload, Shield, UserCheck, UserX, RefreshCcw } from 'lucide-react';

const roleOptions = [
  { label: 'Administrator', value: 'admin' },
  { label: 'Manager', value: 'manager' },
  { label: 'Agent', value: 'agent' },
  { label: 'User', value: 'user' },
];

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Suspended', value: 'suspended' },
];

const UsersModule = () => {
  const [authUsers, setAuthUsers] = useState([]);
  const [customUsers, setCustomUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  // Fetch users from both auth.users and custom users table
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Fetch from custom users table
      const { data: customUsersData, error: customError } = await supabase
        .from('users')
        .select('id, email, full_name, role, phone, avatar_url, status, last_login, created_at')
        .order('created_at', { ascending: false });

      if (customError) {
        console.error('Error fetching custom users:', customError);
      }

      // Fetch from auth.users table (metadata only)
      const { data: authUsersData, error: authError } = await supabase.auth.admin.listUsers({
        page: 1,
        perPage: 1000, // Get all users
      });

      if (authError) {
        console.error('Error fetching auth users:', authError);
      }

      // Process and combine data
      const processedAuthUsers = (authUsersData?.users || []).map(user => ({
        id: user.id,
        email: user.email,
        full_name: user.user_metadata?.name || user.user_metadata?.full_name || user.email?.split('@')[0] || 'Unknown',
        role: 'user', // Default role for auth users
        phone: user.user_metadata?.phone || '',
        avatar_url: user.user_metadata?.avatar_url || user.user_metadata?.photo_url || '',
        status: user.email_confirmed_at ? 'active' : 'inactive',
        last_login: user.last_sign_in_at,
        created_at: user.created_at,
        source: 'auth', // Mark as auth user
        email_confirmed: !!user.email_confirmed_at,
      }));

      setAuthUsers(processedAuthUsers);
      setCustomUsers(customUsersData || []);

    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Combine auth and custom users, with custom users taking precedence
  const allUsers = useMemo(() => {
    const customUsersMap = new Map();
    
    // Add custom users first (they have more complete data)
    customUsers.forEach(user => {
      customUsersMap.set(user.email, {
        ...user,
        source: 'custom'
      });
    });

    // Add auth users that don't exist in custom users
    authUsers.forEach(authUser => {
      if (!customUsersMap.has(authUser.email)) {
        customUsersMap.set(authUser.email, {
          ...authUser,
          source: 'auth'
        });
      }
    });

    return Array.from(customUsersMap.values());
  }, [authUsers, customUsers]);

  const handleEdit = (record) => {
    return {
      ...record,
      permissions: record.permissions || {},
    };
  };

  const getFormFields = (formState = {}) => {
    const fields = [
      { name: 'full_name', label: 'Full Name', placeholder: 'John Doe' },
      { name: 'email', label: 'Email Address', type: 'email', placeholder: 'john.doe@example.com' },
      { name: 'phone', label: 'Phone Number', placeholder: '+256 700 000000' },
      { 
        name: 'role', 
        label: 'User Role', 
        type: 'select', 
        options: roleOptions 
      },
      { 
        name: 'status', 
        label: 'Account Status', 
        type: 'select', 
        options: statusOptions 
      },
      { name: 'avatar_url', label: 'Avatar URL', placeholder: 'https://...' },
      { name: 'permissions', label: 'Permissions (JSON)', type: 'textarea', placeholder: '{"properties": true, "users": false}' },
    ];

    return fields;
  };

  const columns = useMemo(() => [
    { 
      label: 'User', 
      accessor: 'full_name',
      render: (value, record) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden">
            {record.avatar_url ? (
              <img 
                src={record.avatar_url} 
                alt={value} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`w-full h-full bg-stone-300 flex items-center justify-center text-xs font-medium text-stone-600 ${record.avatar_url ? 'hidden' : ''}`}>
              {value ? value.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
            </div>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <p className="font-medium text-stone-900">{value || 'No name'}</p>
              {record.source === 'auth' && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-600">
                  Auth
                </span>
              )}
            </div>
            <p className="text-xs text-stone-500">{record.email}</p>
          </div>
        </div>
      )
    },
    { 
      label: 'Role', 
      accessor: 'role',
      render: (value) => {
        const role = roleOptions.find(r => r.value === value);
        return role ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {role.label}
          </span>
        ) : (
          <span className="text-stone-500">User</span>
        );
      }
    },
    { 
      label: 'Status', 
      accessor: 'status',
      render: (value) => {
        const status = statusOptions.find(s => s.value === value);
        const colorMap = {
          active: 'bg-green-100 text-green-800',
          inactive: 'bg-yellow-100 text-yellow-800',
          suspended: 'bg-red-100 text-red-800'
        };
        return status ? (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorMap[value] || 'bg-stone-100 text-stone-800'}`}>
            {status.label}
          </span>
        ) : (
          <span className="text-stone-500">Unknown</span>
        );
      }
    },
    { 
      label: 'Phone', 
      accessor: 'phone',
      render: (value) => value || 'Not provided'
    },
    { 
      label: 'Last Login', 
      accessor: 'last_login',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Never'
    },
    { 
      label: 'Created', 
      accessor: 'created_at',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Unknown'
    },
  ], []);

  const exportToCSV = () => {
    const headers = ['full_name', 'email', 'phone', 'role', 'status', 'source', 'last_login', 'created_at'];
    const csvContent = [
      headers.join(','),
      ...allUsers.map(record => headers.map(h => {
        let value = record[h] || '';
        if (h === 'last_login') {
          value = record[h] ? new Date(record[h]).toLocaleDateString() : '';
        }
        if (h === 'created_at') {
          value = record[h] ? new Date(record[h]).toLocaleDateString() : '';
        }
        return `"${value}"`;
      }).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getUserStats = () => {
    const total = allUsers.length;
    const active = allUsers.filter(u => u.status === 'active').length;
    const admin = allUsers.filter(u => u.role === 'admin').length;
    const agents = allUsers.filter(u => u.role === 'agent').length;
    const authOnly = allUsers.filter(u => u.source === 'auth').length;
    const customUsers = allUsers.filter(u => u.source === 'custom').length;
    
    return { total, active, admin, agents, authOnly, customUsers };
  };

  const stats = getUserStats();

  return (
    <div className="space-y-6">
      {/* User Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-green-50 to-white p-5 shadow-sm">
          <div className="absolute right-3 top-3 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
            +{stats.authOnly}
          </div>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-green-100 p-3">
              <UserCheck className="h-6 w-6 text-green-700" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-800/80">Total Users</p>
              <p className="text-2xl font-semibold text-stone-900">{stats.total}</p>
              <p className="text-xs text-stone-500">{stats.customUsers} custom / {stats.authOnly} auth</p>
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-green-50 to-white p-5 shadow-sm">
          <div className="absolute right-3 top-3 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
            {Math.round((stats.active / Math.max(stats.total || 1, 1)) * 100)}%
          </div>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-green-100 p-3">
              <UserCheck className="h-6 w-6 text-green-700" />
            </div>
            <div>
              <p className="text-sm font-medium text-green-800/80">Active Users</p>
              <p className="text-2xl font-semibold text-stone-900">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-purple-50 to-white p-5 shadow-sm">
          <div className="absolute right-3 top-3 rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700">
            Core
          </div>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-purple-100 p-3">
              <Shield className="h-6 w-6 text-purple-700" />
            </div>
            <div>
              <p className="text-sm font-medium text-purple-800/80">Administrators</p>
              <p className="text-2xl font-semibold text-stone-900">{stats.admin}</p>
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-orange-50 to-white p-5 shadow-sm">
          <div className="absolute right-3 top-3 rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
            Field
          </div>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-orange-100 p-3">
              <UserCheck className="h-6 w-6 text-orange-700" />
            </div>
            <div>
              <p className="text-sm font-medium text-orange-800/80">Agents</p>
              <p className="text-2xl font-semibold text-stone-900">{stats.agents}</p>
            </div>
          </div>
        </div>
      </div>

      {/* User Management Resource Manager */}
      {loading ? (
        <div className="bg-white p-8 rounded-xl border border-stone-200 shadow-sm text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-lime-500"></div>
          <p className="text-stone-600">Loading users...</p>
        </div>
      ) : (
        <ResourceManager
          table="users"
          title="User Management"
          description="Manage user accounts, roles, and permissions for the dashboard."
          columns={columns}
          formFields={getFormFields}
          searchable={true}
          searchPlaceholder="Search by name, email, or role..."
          searchFields={['full_name', 'email', 'role', 'status']}
          paginated={true}
          itemsPerPage={20}
          externalRecords={allUsers}
          defaultValues={{
            full_name: '',
            email: '',
            phone: '',
            role: 'user',
            status: 'active',
            avatar_url: '',
            permissions: '{}',
          }}
          transformBeforeSave={(payload) => {
            try {
              // Try to parse permissions as JSON
              if (typeof payload.permissions === 'string') {
                payload.permissions = JSON.parse(payload.permissions || '{}');
              }
            } catch (error) {
              // If JSON parsing fails, set to empty object
              payload.permissions = {};
            }
            return payload;
          }}
        onEdit={handleEdit}
        onReady={({ refresh }) => {
          // Override refresh to call our fetchUsers function
          window.refreshUsers = fetchUsers;
        }}
        onSaveSuccess={fetchUsers}
        extraButtons={[
          { label: 'Export CSV', onClick: exportToCSV, icon: Download, primary: true },
          { label: 'Refresh Users', onClick: fetchUsers, icon: RefreshCcw },
        ]}
      />
      )}
    </div>
  );
};

export default UsersModule;
